import { useState, useRef, useCallback } from 'react';

interface PreviewFile {
  id: string;
  file: File;
  url: string;
}

export default function PhotoUpload() {
  const [previews, setPreviews] = useState<PreviewFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const newItems: PreviewFile[] = [];
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) return;
      if (previews.length + newItems.length >= 5) return;
      newItems.push({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        url: URL.createObjectURL(file),
      });
    });
    setPreviews((prev) => [...prev, ...newItems]);
  }, [previews]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const handleRemove = (id: string) => {
    setPreviews((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  return (
    <div className="flex flex-col gap-3">
      {/* 드래그 앤 드롭 영역 */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`w-full border-2 border-dashed rounded-md px-4 py-8 flex flex-col items-center justify-center gap-2 cursor-pointer transition-colors ${
          dragging ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50 hover:border-yellow-300 hover:bg-yellow-50/50'
        }`}
      >
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-yellow-100">
          <i className="ri-image-add-line text-xl text-yellow-500"></i>
        </div>
        <p className="text-sm font-semibold text-gray-700">사진을 드래그하거나 클릭해서 업로드</p>
        <p className="text-xs text-gray-400">JPG, PNG, HEIC 등 이미지 파일 최대 5장</p>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {/* 미리보기 */}
      {previews.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {previews.map((p) => (
            <div key={p.id} className="relative group rounded-md overflow-hidden bg-gray-100" style={{ aspectRatio: '1/1' }}>
              <img
                src={p.url}
                alt="현장사진 미리보기"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleRemove(p.id)}
                className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <i className="ri-close-line text-white text-xs"></i>
              </button>
            </div>
          ))}
          {previews.length < 5 && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex items-center justify-center rounded-md border-2 border-dashed border-gray-200 bg-gray-50 hover:border-yellow-300 hover:bg-yellow-50/50 transition-colors cursor-pointer"
              style={{ aspectRatio: '1/1' }}
            >
              <i className="ri-add-line text-gray-400 text-xl"></i>
            </button>
          )}
        </div>
      )}

      {previews.length > 0 && (
        <p className="text-xs text-gray-400">
          <i className="ri-information-line mr-1"></i>
          사진은 담당자에게 카카오톡 또는 문자로 별도 전송해 주시면 더 정확한 견적이 가능합니다.
          <br />
          <span className="text-yellow-600 font-semibold">010-4016-8890</span>으로 보내주세요.
        </p>
      )}
    </div>
  );
}
